import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronUp, ChevronDown, X, Search, Replace, Info } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";

const BLOCK_SEL = "p,div,li,h1,h2,h3,h4,h5,h6,td,th,blockquote,pre";

export function findRanges(root, q, { matchCase, wholeWord }) {
  if (!root || !q) return [];
  const nodes = [];
  let text = "";
  let last = null;
  const w = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode: (n) => (n.parentElement.closest('[contenteditable="false"]') ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT),
  });
  let n;
  while ((n = w.nextNode())) {
    const b = n.parentElement.closest(BLOCK_SEL);
    if (last && b !== last) text += "\u0000";
    last = b;
    nodes.push({ n, s: text.length });
    text += n.data;
  }
  const esc = q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const re = new RegExp(wholeWord ? `(?<![\\p{L}\\p{N}_])${esc}(?![\\p{L}\\p{N}_])` : esc, "gu" + (matchCase ? "" : "i"));
  const at = (off, end) => {
    for (const x of nodes) {
      const len = x.n.data.length;
      if (end ? off > x.s && off <= x.s + len : off >= x.s && off < x.s + len) return [x.n, off - x.s];
    }
    return null;
  };
  const out = [];
  let m;
  while ((m = re.exec(text))) {
    if (!m[0].length) { re.lastIndex++; continue; }
    const a = at(m.index, false);
    const b = at(m.index + m[0].length, true);
    if (!a || !b) continue;
    const r = document.createRange();
    r.setStart(a[0], a[1]);
    r.setEnd(b[0], b[1]);
    out.push(r);
  }
  return out;
}

const hasHL = () => typeof window.CSS !== "undefined" && window.CSS.highlights && typeof window.Highlight !== "undefined";
const paint = (m, i) => {
  if (!hasHL()) return;
  CSS.highlights.set("find-all", new window.Highlight(...m));
  if (m[i]) CSS.highlights.set("find-current", new window.Highlight(m[i]));
  else CSS.highlights.delete("find-current");
};
const clearPaint = () => { if (hasHL()) { CSS.highlights.delete("find-all"); CSS.highlights.delete("find-current"); } };

const scrollToRange = (r) => {
  const box = document.querySelector(".canvas-scroll");
  if (!box || !r) return;
  const rr = r.getBoundingClientRect();
  const br = box.getBoundingClientRect();
  if (rr.top < br.top + 40 || rr.bottom > br.bottom - 40) box.scrollBy({ top: rr.top - br.top - br.height / 3, behavior: "smooth" });
};

export const FindReplace = ({ rootRef, onClose }) => {
  const [q, setQ] = useState("");
  const [rep, setRep] = useState("");
  const [mc, setMc] = useState(false);
  const [ww, setWw] = useState(false);
  const [matches, setMatches] = useState([]);
  const [idx, setIdx] = useState(0);
  const inputRef = useRef();
  const opts = { matchCase: mc, wholeWord: ww };

  const run = useCallback((keep = 0) => {
    const m = findRanges(rootRef.current, q, { matchCase: mc, wholeWord: ww });
    const i = m.length ? Math.min(Math.max(keep, 0), m.length - 1) : 0;
    setMatches(m);
    setIdx(i);
    paint(m, i);
    return { m, i };
  }, [q, mc, ww, rootRef]);

  useEffect(() => { const { m, i } = run(0); scrollToRange(m[i]); }, [run]);
  useEffect(() => { inputRef.current?.focus(); return clearPaint; }, []);
  useEffect(() => {
    const el = rootRef.current;
    let t;
    const h = () => { clearTimeout(t); t = setTimeout(() => run(idx), 250); };
    el?.addEventListener("input", h);
    return () => { el?.removeEventListener("input", h); clearTimeout(t); };
  }, [run, idx, rootRef]);

  const go = (d) => {
    if (!matches.length) return;
    const i = (idx + d + matches.length) % matches.length;
    setIdx(i);
    paint(matches, i);
    scrollToRange(matches[i]);
  };
  const changed = () => rootRef.current.dispatchEvent(new Event("input", { bubbles: true }));
  const put = (r) => { r.deleteContents(); if (rep) r.insertNode(document.createTextNode(rep)); };

  const replaceOne = () => {
    if (!matches.length) return;
    put(matches[idx]);
    rootRef.current.normalize();
    changed();
    const { m, i } = run(idx);
    scrollToRange(m[i]);
  };
  const replaceAll = () => {
    const m = findRanges(rootRef.current, q, opts);
    if (!m.length) return toast("No matches to replace");
    [...m].reverse().forEach(put);
    rootRef.current.normalize();
    changed();
    toast.success(`Replaced ${m.length} occurrence${m.length > 1 ? "s" : ""}`);
    run(0);
  };

  return (
    <aside className="w-[320px] shrink-0 border-l bg-card flex flex-col" data-testid="find-replace-panel">
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <div className="flex items-center gap-2 font-semibold text-sm"><Search className="h-4 w-4 text-primary" />Find & Replace</div>
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose} data-testid="find-close-btn"><X className="h-4 w-4" /></Button>
      </div>
      <div className="p-4 space-y-4 overflow-y-auto">
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-muted-foreground">Find</label>
          <div className="relative">
            <Input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Text to find" className="pr-20" data-testid="find-input"
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); go(e.shiftKey ? -1 : 1); } }} />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs tabular-nums text-muted-foreground" data-testid="find-count">
              {q ? (matches.length ? `${idx + 1} of ${matches.length}` : "0 found") : ""}
            </span>
          </div>
          <div className="flex gap-1.5">
            <Button variant="outline" size="sm" className="flex-1" onClick={() => go(-1)} disabled={!matches.length} data-testid="find-prev-btn"><ChevronUp className="h-4 w-4 mr-1" />Previous</Button>
            <Button variant="outline" size="sm" className="flex-1" onClick={() => go(1)} disabled={!matches.length} data-testid="find-next-btn"><ChevronDown className="h-4 w-4 mr-1" />Next</Button>
          </div>
        </div>
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-muted-foreground">Replace with</label>
          <Input value={rep} onChange={(e) => setRep(e.target.value)} placeholder="Replacement text (leave empty to delete)" data-testid="replace-input" />
          <div className="flex gap-1.5">
            <Button size="sm" variant="outline" className="flex-1" onClick={replaceOne} disabled={!matches.length} data-testid="replace-one-btn"><Replace className="h-4 w-4 mr-1" />Replace</Button>
            <Button size="sm" className="flex-1" onClick={replaceAll} disabled={!matches.length} data-testid="replace-all-btn">Replace all</Button>
          </div>
        </div>
        <div className="space-y-2.5">
          <label className="flex items-center gap-2 text-sm cursor-pointer"><Checkbox checked={mc} onCheckedChange={(v) => setMc(!!v)} data-testid="find-match-case" />Match case</label>
          <label className="flex items-center gap-2 text-sm cursor-pointer"><Checkbox checked={ww} onCheckedChange={(v) => setWw(!!v)} data-testid="find-whole-word" />Whole words only</label>
        </div>
        <div className="rounded-lg border bg-muted/40 p-3.5 text-[12.5px] leading-relaxed text-muted-foreground space-y-2" data-testid="find-replace-help">
          <div className="flex items-center gap-1.5 font-semibold text-foreground text-xs uppercase tracking-wider"><Info className="h-3.5 w-3.5 text-primary" />How it works</div>
          <p><b className="text-foreground">1. Find</b> — type a word or phrase. Every match is highlighted in <span className="px-1 rounded bg-amber-200 text-amber-900">yellow</span> and the counter shows how many were found.</p>
          <p><b className="text-foreground">2. Step through</b> — use Previous / Next (or Enter and Shift+Enter). The current match is shown in <span className="px-1 rounded bg-amber-500 text-white">orange</span>.</p>
          <p><b className="text-foreground">3. Replace</b> — type the new text, then <i>Replace</i> changes only the current match, while <i>Replace all</i> changes every match at once.</p>
          <p><b className="text-foreground">Options</b> — <i>Match case</i> finds exact capitalisation only; <i>Whole words</i> skips matches inside longer words (“cat” won’t match “category”). Press Ctrl+Z to undo typing; shortcut Ctrl+F opens this panel.</p>
        </div>
      </div>
    </aside>
  );
};
